<template>
  <page-template :is-loading="isLoading" :data-exists="tabs.length > 0">
    <template #pageHeadline>
      {{ $t('certificate.pageHeadline') }}
    </template>
    <template #pageContent>
      <div class="certificates-tabs-wrapper">
        <tabs-wrapper :tabs="tabs">
          <template #[tabs.at(0).title]>
            <certificates-list-server-tab />
          </template>
          <template #[tabs.at(1).title]>
            <certificates-list-client-ca-tab />
          </template>
          <template #[tabs.at(2).title]>
            <certificates-list-server-to-backend-mtls-tab />
          </template>
          <template #[tabs.at(3).title]>
            <certificates-list-server-to-backend-ca-tab />
          </template>
        </tabs-wrapper>
      </div>
    </template>
  </page-template>
</template>
<script lang="ts">
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import { defineComponent } from 'vue';
import TabsWrapper from 'ui-component-library/rbz/components/TabsWrapper.vue';
import RequestsUtils from '@/helpers/RequestsUtils';
import { ServerGroup } from '@/types';
import { useBranchesStore } from '@/store/BranchesStore';
import { mapStores } from 'pinia';
import { Tab } from 'ui-component-library/rbz/types';
import CertificatesListServerTab from '@/components/CertificatesListServerTab.vue';
import CertificatesListClientCaTab from '@/components/CertificatesListClientCaTab.vue';
import CertificatesListServerToBackendMtlsTab from '@/components/CertificatesListServerToBackendMtlsTab.vue';
import CertificatesListServerToBackendCaTab from '@/components/CertificatesListServerToBackendCaTab.vue';

export default defineComponent({
  name: 'CertificatesList',
  data() {
    return {
      validNlb: false,
      pageLoadingCounter: null,
      serverGroups: null as ServerGroup[],
      isClientCaCertEnabled: false,
      isServerToBackendMtlsCertEnabled: false,
      isServerToBackendCaCertEnabled: false,
    };
  },
  components: {
    CertificatesListServerTab,
    CertificatesListClientCaTab,
    CertificatesListServerToBackendMtlsTab,
    CertificatesListServerToBackendCaTab,
    TabsWrapper,
    PageTemplate,
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    tabs(): Tab[] {
      return [
        {
          title: this.$t('certificate.serverCertificate.plural'),
        },
        {
          title: this.$t('certificate.clientCaCertificate.plural'),
          hidden: !this.validNlb || !this.isClientCaCertEnabled,
        },
        {
          title: this.$t('certificate.serverToBackendMtlsCertificate.plural'),
          hidden: !this.isServerToBackendMtlsCertEnabled,
        },
        {
          title: this.$t('certificate.serverToBackendCaCertificate.plural'),
          hidden: !this.isServerToBackendCaCertEnabled,
        },
      ];
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadNlb() {
      this.setPageLoadingStatus(true);
      const url = 'tools/versions';
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
      });
      if (response?.data) {
        this.validNlb = response.data.nlb;
      }
      this.setPageLoadingStatus(false);
    },

    async loadNamespaces() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `db/system`,
      });
      const featureToggle = response?.data?.['feature-toggle'];
      if (featureToggle) {
        this.isClientCaCertEnabled = !!featureToggle['ssl-client-to-v5-client-ca-certificate'];
        this.isServerToBackendMtlsCertEnabled =
          !!featureToggle['ssl-server-to-backend-mtls-certificate'];
        this.isServerToBackendCaCertEnabled =
          !!featureToggle['ssl-server-to-backend-ca-certificate'];
      }
      this.setPageLoadingStatus(false);
    },
  },
  async beforeMount() {
    const loadNlbPromise = this.loadNlb();
    const loadNamespacesPromise = this.loadNamespaces();
    await Promise.all([await loadNlbPromise, await loadNamespacesPromise]);
  },
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

:deep(.certificates-tabs-wrapper) {
  tr:last-child td {
    border-bottom-width: 1px;
  }

  .tab {
    flex-grow: 0;
  }
}
</style>
